import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, imgUrl } from "@/lib/api";
import { Reveal, Overline } from "@/components/Shared";
import { ArrowLeft, MapPin, Phone, ExternalLink } from "lucide-react";

export default function NovitaDettaglio() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/projects/${id}`)
      .then((r) => setP(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <main className="grid min-h-[60vh] place-items-center pt-28">
        <div className="text-center">
          <p className="font-serif text-2xl text-slate-900">Progetto non trovato</p>
          <Link to="/lavori?tab=novita" data-testid="back-link" className="mt-4 inline-block text-xs uppercase tracking-[0.25em] text-gold">
            ← Torna alle novità
          </Link>
        </div>
      </main>
    );
  }

  if (!p) {
    return <div className="grid min-h-[60vh] place-items-center pt-28 text-sm text-slate-400">Caricamento…</div>;
  }

  const gallery = p.plans || [];

  return (
    <main data-testid="novita-detail-page" className="pt-28">
      <div className="mx-auto max-w-6xl px-5 pt-6 sm:px-8">
        <Link
          to="/lavori?tab=novita"
          data-testid="back-to-novita"
          className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-slate-500 transition-colors hover:text-gold"
        >
          <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" /> Torna alle novità
        </Link>
      </div>

      <section className="mx-auto max-w-6xl px-5 py-8 sm:px-8">
        <Reveal>
          <div className="relative overflow-hidden border border-slate-200 bg-obsidian">
            <img
              src={imgUrl(p.image)}
              alt={p.name}
              data-testid="detail-main-image"
              className="max-h-[75vh] w-full object-cover"
            />
            {p.status && (
              <span className="absolute left-5 top-5 border border-gold/40 bg-white/90 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-gold backdrop-blur-sm">
                {p.status}
              </span>
            )}
          </div>
        </Reveal>

        <div className="grid gap-12 py-14 lg:grid-cols-[1fr_300px]">
          <Reveal>
            <Overline>{p.category || "Novità in cantiere"}</Overline>
            <h1 className="mt-4 font-serif text-4xl font-light tracking-tight text-slate-900 sm:text-5xl">{p.name}</h1>
            {p.location && (
              <p className="mt-3 flex items-center gap-2 text-sm text-slate-500">
                <MapPin className="h-4 w-4 text-gold" /> {p.location}
              </p>
            )}
            {p.description && (
              <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-600">{p.description}</p>
            )}
          </Reveal>
          <Reveal delay={0.15}>
            <aside className="border border-slate-200 bg-obsidian p-7">
              {p.sale_link ? (
                <>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand">In vendita</p>
                  <p className="mt-4 text-sm leading-relaxed text-slate-600">
                    Questa palazzina è in vendita presso l'agenzia immobiliare. Vedi l'annuncio completo:
                  </p>
                  <a
                    href={p.sale_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="detail-agency-cta"
                    className="mt-5 flex items-center gap-3 bg-brand px-5 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-gold"
                  >
                    <ExternalLink className="h-4 w-4" /> Vedi l'annuncio
                  </a>
                </>
              ) : (
                <>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand">Ti interessa?</p>
                  <p className="mt-4 text-sm leading-relaxed text-slate-600">
                    Per informazioni su questo progetto contattaci direttamente:
                  </p>
                  <Link
                    to="/#contatti-footer"
                    data-testid="detail-contacts-cta"
                    className="mt-5 flex items-center gap-3 bg-gold px-5 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-brand"
                  >
                    <Phone className="h-4 w-4" /> Contatti
                  </Link>
                </>
              )}
            </aside>
          </Reveal>
        </div>

        {gallery.length > 0 && (
          <Reveal>
            <div className="border-t border-slate-200 pt-12 pb-20">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand">Altre viste e piantine</p>
              <div className="mt-6 grid gap-6 sm:grid-cols-2">
                {gallery.map((url, i) => (
                  <a key={i} href={imgUrl(url)} target="_blank" rel="noopener noreferrer" data-testid={`plan-image-${i}`} className="group">
                    <img
                      src={imgUrl(url)}
                      alt={`${p.name} — vista ${i + 1}`}
                      loading="lazy"
                      className="w-full border border-slate-200 object-cover transition-shadow duration-500 group-hover:shadow-xl"
                    />
                  </a>
                ))}
              </div>
            </div>
          </Reveal>
        )}
      </section>
    </main>
  );
}
